import React, { useRef, useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { DIMENSIONS } from '../../constants/gameConstants';
import StimulusDisplay from './StimulusDisplay';
import { breakpoints } from '../../../styles/theme';

const GameArea = ({ 
  activeDimensions, 
  currentStimulus, 
  stimulusVisible, 
  resetGame,
  nValue,
  onDimensionPress,
  pressedInCurrentTrial = new Set()
}) => {
  const keyboardShortcutsRef = useRef(null);
  const displayRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [activePress, setActivePress] = useState(null); // Track which button is currently being pressed
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isTabletOrSmaller = windowWidth <= parseInt(breakpoints.tablet);
  
  // Scroll to position keyboard shortcuts fully visible at top when component mounts
  useEffect(() => {
    // Small delay to ensure layout is complete
    const timer = setTimeout(() => {
      if (keyboardShortcutsRef.current) {
        // Get the element's position and dimensions
        const rect = keyboardShortcutsRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // Calculate target position to show full element with generous padding from top
        const targetPosition = rect.top + scrollTop - 52; // 52px padding from top to ensure full visibility
        
        window.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []); // Only run once when component mounts
  
  // Handle touch button press
  const handleTouchPress = (dimension) => {
    if (onDimensionPress) {
      onDimensionPress(dimension);
      
      // Visual feedback: flash the button
      setActivePress(dimension);
      setTimeout(() => setActivePress(null), 150);
    }
  };
  
  return (
    <div style={{
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      {/* Level Display & Keyboard Shortcuts */}
      {isTabletOrSmaller ? (
        // Mobile: Just a simple badge at the top
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '0.1rem auto 0.25rem auto'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            padding: '0.3rem 0.7rem',
            borderRadius: '8px',
            fontWeight: '700',
            fontSize: '0.75rem',
            color: 'white',
            boxShadow: '0 2px 8px rgba(96, 165, 250, 0.3)'
          }}>
            {nValue}-BACK
          </div>
        </div>
      ) : (
        // Desktop: Full keyboard shortcuts box
        <div 
          ref={keyboardShortcutsRef}
          style={{
            maxWidth: '800px',
            margin: '0 auto 0.5rem auto',
          background: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          padding: '0.75rem 1rem',
          border: '1px solid rgba(100, 116, 139, 0.2)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {/* Current Level Badge */}
          <div style={{
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            padding: '0.4rem 0.75rem',
            borderRadius: '6px',
            fontWeight: '700',
            fontSize: '0.85rem',
            color: 'white',
            marginRight: '0.5rem'
          }}>
            {nValue}-BACK
          </div>
        
        {/* Only show keyboard shortcuts on desktop */}
        <>
          <span style={{
            fontSize: '0.75rem',
            color: '#94a3b8',
            fontWeight: '600',
            marginRight: '0.25rem'
          }}>
            PRESS:
          </span>
          {activeDimensions.map(dim => (
            <div key={dim} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              fontSize: '0.8rem'
            }}>
              <kbd style={{
                background: DIMENSIONS[dim].color,
                color: 'white',
                padding: '0.2rem 0.5rem',
                borderRadius: '4px',
                fontWeight: '700',
                fontSize: '0.75rem',
                minWidth: '1.5rem',
                textAlign: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                {DIMENSIONS[dim].key.toUpperCase()}
              </kbd>
              <span style={{
                color: '#cbd5e1',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}>
                {DIMENSIONS[dim].name}
              </span>
            </div>
          ))}
        </>
      </div>
      )}

      {/* Main Display */}
      <div 
        ref={displayRef}
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          marginBottom: '0.75rem'
        }}
      >
        <StimulusDisplay 
          currentStimulus={currentStimulus}
          activeDimensions={activeDimensions}
          stimulusVisible={stimulusVisible}
        />
      </div>

      {/* Touch Buttons for Tablet/Mobile - Above End Round Button */}
      {isTabletOrSmaller && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: activeDimensions.length <= 2 ? `repeat(${activeDimensions.length}, 1fr)` : 
                              activeDimensions.length <= 4 ? 'repeat(2, 1fr)' : 
                              activeDimensions.length <= 6 ? 'repeat(3, 1fr)' :
                              'repeat(4, 1fr)',
          gap: '0.75rem',
          maxWidth: '800px',
          margin: '0 auto 1rem auto',
          padding: '0'
        }}>
          {activeDimensions.map(dimension => {
            const isPressed = pressedInCurrentTrial.has(dimension);
            const isActivePress = activePress === dimension;
            const baseColor = DIMENSIONS[dimension].color;
            
            // Calculate darker shade for pressed state (reduce brightness by ~30%)
            const getDarkerColor = (color) => {
              // Simple darkening by reducing the brightness
              const hex = color.replace('#', '');
              const r = parseInt(hex.substr(0, 2), 16);
              const g = parseInt(hex.substr(2, 2), 16);
              const b = parseInt(hex.substr(4, 2), 16);
              const darkenFactor = 0.7;
              return `rgb(${Math.round(r * darkenFactor)}, ${Math.round(g * darkenFactor)}, ${Math.round(b * darkenFactor)})`;
            };
            
            return (
              <button
                key={dimension}
                onClick={() => handleTouchPress(dimension)}
                onTouchStart={(e) => {
                  e.preventDefault();
                  handleTouchPress(dimension);
                }}
                style={{
                  background: isActivePress 
                    ? getDarkerColor(baseColor) // Much darker when actively pressing
                    : isPressed
                    ? `linear-gradient(135deg, ${getDarkerColor(baseColor)} 0%, ${getDarkerColor(baseColor)}DD 100%)` // Darker when already pressed
                    : `linear-gradient(135deg, ${baseColor} 0%, ${baseColor}CC 100%)`, // Normal state
                  border: isPressed ? '3px solid rgba(255, 255, 255, 0.4)' : 'none', // Subtle border for pressed state
                  borderRadius: '10px',
                  padding: activeDimensions.length > 6 ? '0.85rem 0.6rem' : '1rem 0.75rem',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: activeDimensions.length > 6 ? '0.75rem' : '0.85rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: isPressed 
                    ? '0 2px 6px rgba(0,0,0,0.4), inset 0 2px 8px rgba(0,0,0,0.3)' // Inset shadow for pressed
                    : '0 4px 12px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25rem',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  minHeight: activeDimensions.length > 6 ? '52px' : '60px',
                  transform: isActivePress ? 'scale(0.95)' : 'scale(1)', // Scale down when actively pressing
                  opacity: isPressed ? 0.85 : 1 // Slightly transparent when already pressed
                }}
              >
                <div style={{
                  fontSize: activeDimensions.length > 6 ? '0.7rem' : '0.8rem',
                  textAlign: 'center',
                  lineHeight: '1.2',
                  wordBreak: 'break-word',
                  hyphens: 'auto'
                }}>
                  {DIMENSIONS[dimension].name}
                </div>
                {/* Small checkmark indicator when pressed */}
                {isPressed && !isActivePress && (
                  <div style={{
                    fontSize: '0.6rem',
                    opacity: 0.9,
                    marginTop: '2px'
                  }}>
                    ✓
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* End Round Button */}
      <div style={{
        maxWidth: '800px',
        margin: '0.5rem auto 1rem auto'
      }}>
        <button
          onClick={resetGame}
          style={{
            width: '100%',
            background: 'rgba(239, 68, 68, 0.2)',
            border: '2px solid #ef4444',
            borderRadius: '12px',
            padding: '0.75rem',
            color: '#ef4444',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.3)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.2)'}
        >
          <RotateCcw size={18} />
          End Round
        </button>
      </div>

      {/* Key Guide - Only shown on desktop */}
      {!isTabletOrSmaller && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid rgba(100, 116, 139, 0.3)'
        }}>
          <h4 style={{
            margin: '0 0 1rem 0',
            fontSize: '0.9rem',
            fontWeight: '600',
            color: '#94a3b8'
          }}>
            KEYBOARD SHORTCUTS
          </h4>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            {activeDimensions.map(dimension => (
              <div key={dimension} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <kbd style={{
                  background: DIMENSIONS[dimension].color,
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  minWidth: '24px',
                  textAlign: 'center'
                }}>
                  {DIMENSIONS[dimension].key.toUpperCase()}
                </kbd>
                <span style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>
                  {DIMENSIONS[dimension].name} Match
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameArea;

